import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'

import {
  getRecipeSubsection,
  parseISO8601Duration,
  formatIso8601Duration,
} from 'libs/utils'
import { createRateArticle, rateArticle } from 'libs/client/api/articles'

import { trackIngredientClick } from './events'
import EmailRecipeButton from './EmailRecipeButton'
import Metric from './Metric'
import Ratings from './Ratings'

const Recipe = () => {
  const { article } = useStaticData()
  const { t } = useTranslation('@mc/next')
  const [userRating, setUserRating] = useState(null)

  const recipe = getRecipeSubsection(article.articleSubsections)

  const onRate = useCallback(
    (rating, currentRating) => {
      window.localStorage.setItem(`rating-${article.id}`, rating)

      if (userRating) {
        rateArticle(article, rating, currentRating)
      } else {
        setUserRating(rating)
        createRateArticle(article, rating)
      }
    },
    [article, userRating],
  )

  useEffect(() => {
    const userRatingFromLocalStorage = JSON.parse(
      window.localStorage.getItem(`rating-${article.id}`),
    )
    if (userRatingFromLocalStorage) {
      setUserRating(userRatingFromLocalStorage)
    }

    if (recipe) {
      trackIngredientClick(recipe)
    }
  }, [recipe, article.id])

  if (!recipe) return <></>

  return (
    <section id={recipe.id} className='mc-my-10 mc-bg-gray-700 mc-p-10'>
      <h2 className='mc-text-h2 mc-mb-8'>{recipe.title}</h2>
      <div className='row mc-mb-4'>
        <EmailRecipeButton />
        <Ratings userRating={userRating} onRate={onRate} />
      </div>
      <div className='row mc-mb-4'>
        <Metric name={t('article.metrics.makes')} value={recipe.recipeYield} />
        {recipe.prepTime && (
          <Metric
            name={t('article.metrics.prepTime')}
            value={formatIso8601Duration(parseISO8601Duration(recipe.prepTime))}
          />
        )}
        {recipe.totalTime && (
          <Metric
            name={t('article.metrics.totalTime')}
            value={formatIso8601Duration(
              parseISO8601Duration(recipe.totalTime),
            )}
          />
        )}
        {recipe.cookTime && (
          <Metric
            name={t('article.metrics.cookTime')}
            value={formatIso8601Duration(parseISO8601Duration(recipe.cookTime))}
          />
        )}
      </div>
      <div className='mc-text-large highlight-shareable'>
        <h3 className='mc-text--bold mc-text-h3 mc-mb-4'>
          {t('article.subsections.recipe.title')}
        </h3>
        <div
          className='mc-mb-4'
          dangerouslySetInnerHTML={{ __html: recipe.recipeIngredient }}
        />
        <div
          className='mc-mb-4'
          dangerouslySetInnerHTML={{ __html: recipe.recipeInstructions }}
        />
      </div>
    </section>
  )
}

export default Recipe
